import type { ReactNode } from "react";

import { classes } from "./classes.js";
import {
  BANK_BAD_CALLOUT,
  BANK_BAD_BORDER,
  BANK_BG_BRAND_WASH,
  BANK_BG_SURFACE_2,
  BANK_BORDER,
  BANK_FS_READ,
  BANK_OK_CALLOUT,
  BANK_OK_BORDER,
  BANK_RADIUS_MD,
  BANK_TEXT,
  BANK_WARN_CALLOUT,
  BANK_WARN_BORDER,
} from "./tokens.js";

/**
 * `F-0b`: the block that says one thing to the person, in the bank (prototype `.callout`).
 *
 * `info · ok · warn · bad · proposition`. It replaces the hand-written amber, rose and emerald blocks of the
 * composer, the decision and the conditions. `proposition` is the agent's proposal and is drawn DASHED: Hakili
 * proposes and people decide (D-1020-7), so a proposal must not look like a finding. `bad` is a live `alert`; the
 * others are notes, which a reader meets in order and is not interrupted by.
 *
 * Zero French here: the sentence is the caller's, from the catalogue.
 */
export type CalloutTone = "info" | "ok" | "warn" | "bad" | "proposition";

export interface CalloutProps {
  readonly tone?: CalloutTone;
  readonly children: ReactNode;
  readonly icon?: ReactNode;
  readonly className?: string;
  readonly testId?: string;
}

const TONES: Readonly<Record<CalloutTone, string>> = {
  info: classes(BANK_BG_BRAND_WASH, BANK_TEXT, BANK_BORDER),
  ok: classes(BANK_OK_CALLOUT, BANK_OK_BORDER),
  warn: classes(BANK_WARN_CALLOUT, BANK_WARN_BORDER),
  bad: classes(BANK_BAD_CALLOUT, BANK_BAD_BORDER),
  proposition: classes(BANK_BG_SURFACE_2, BANK_TEXT, BANK_WARN_BORDER, "border-dashed"),
};

export function Callout({
  tone = "info",
  children,
  icon,
  className,
  testId,
}: CalloutProps): React.JSX.Element {
  return (
    <div
      role={tone === "bad" ? "alert" : "note"}
      data-kit="callout"
      data-tone={tone}
      data-testid={testId}
      className={classes(
        "flex items-start gap-3 border px-4 py-3",
        BANK_RADIUS_MD,
        BANK_FS_READ,
        TONES[tone],
        className,
      )}
    >
      {icon === undefined ? null : <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
